import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight, ArrowUpRight, Globe, Check } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import DebtDroneMark from '@/components/icons/DebtDroneMark';
import { cn } from '@/lib/utils';
import { CLI_DOCS_URL } from '@/lib/contact';
import { useCtaTracker, useNavTracker, type CtaDescriptor } from '@/hooks/useCtaTracker';
import type { NavPlacement } from '@/lib/analytics/funnel';
import { PublicContainer } from './PublicContainer';

type Lang = 'en' | 'es' | 'de';

const LANGUAGES: { code: Lang; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'de', label: 'Deutsch' },
];

const WAITLIST_MODE = import.meta.env.VITE_WAITLIST_MODE === 'true';


interface PublicNavItem {
  label: string;
  to: string;
  external?: boolean;
  /** Set on the items that are funnel destinations; navigation links are not reported. */
  cta?: Omit<CtaDescriptor, 'placement'>;
}

const CLI_NAV_ITEM: PublicNavItem = {
  label: 'CLI',
  to: CLI_DOCS_URL,
  external: true,
  cta: { action: 'cli_docs', priority: 'secondary' },
};

// Labels name the intent rather than the category, and two "docs" destinations
// sitting side by side are told apart. Changelog moved to the footer: it serves
// existing users, and seven top-level items was too many.
const NAV_LINKS: PublicNavItem[] = WAITLIST_MODE
  ? [{ label: 'How it works', to: '/#features' }, CLI_NAV_ITEM]
  : [
      { label: 'How it works', to: '/#features' },
      { label: 'Pricing', to: '/pricing' },
      { label: 'Product docs', to: '/docs', cta: { action: 'product_docs', priority: 'secondary' } },
      CLI_NAV_ITEM,
      { label: 'Roadmap', to: '/roadmap' },
      { label: 'Blog', to: '/blog' },
    ];

function PublicNavLink({ item, className, placement, onClick }: {
  item: PublicNavItem;
  className: string;
  placement: 'header_nav' | 'mobile_nav';
  onClick?: () => void;
}) {
  const trackCta = useCtaTracker();
  const trackNav = useNavTracker();
  // One handler for both branches, so a link cannot be reported twice or, when
  // the menu-closing callback is the only one, not at all. A funnel
  // destination reports as a CTA; everything else in the chrome reports as
  // navigation, so no chrome link is silently unmeasured.
  const handleClick = () => {
    if (item.cta) trackCta({ ...item.cta, placement });
    else trackNav({ to: item.to, placement });
    onClick?.();
  };
  if (item.external) {
    return (
      <a href={item.to} className={className} onClick={handleClick} target="_blank" rel="noreferrer">
        {item.label}
        <ArrowUpRight className="ml-0.5 inline h-3 w-3 align-[-0.1em]" aria-hidden="true" />
        <span className="sr-only"> (opens in a new tab)</span>
      </a>
    );
  }

  return <Link to={item.to} className={className} onClick={handleClick}>{item.label}</Link>;
}

/**
 * A footer link, instrumented once for all of them.
 *
 * The footer previously carried a dozen hand-written anchors, which is exactly
 * the shape where one gets added without an event. Declaring `cta` marks a
 * funnel destination; everything else reports as navigation, resolved from the
 * link target.
 */
function FooterLink({ to, external, cta, children }: {
  to: string;
  external?: boolean;
  cta?: Omit<CtaDescriptor, 'placement'>;
  children: React.ReactNode;
}) {
  const trackCta = useCtaTracker();
  const trackNav = useNavTracker();
  const className = 'rounded-sm text-sm text-muted-foreground transition-colors hover:text-foreground active:text-lime';

  const handleClick = () => {
    if (cta) trackCta({ ...cta, placement: 'footer' });
    else trackNav({ to, placement: 'footer' });
  };

  if (external) {
    return <a href={to} className={className} onClick={handleClick}>{children}</a>;
  }

  return <Link to={to} className={className} onClick={handleClick}>{children}</Link>;
}

/**
 * The wordmark, which is also the way most visitors go back to the landing
 * page. It appears twice — in the header and in the footer — so it takes its
 * placement rather than assuming one.
 */
function Logo({ placement }: { placement: Extract<NavPlacement, 'header_nav' | 'footer'> }) {
  const trackNav = useNavTracker();

  return (
    <Link
      to="/"
      className="flex items-center gap-2"
      onClick={() => trackNav({ to: '/', placement })}
    >
      <DebtDroneMark className="h-8 w-8" />
      <span className="text-lg font-semibold tracking-tight">DebtDrone</span>
    </Link>
  );
}

const PublicLayout: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const { user } = useAuth();
  const { language, setLanguage } = useLanguage();
  const location = useLocation();
  const trackCta = useCtaTracker();
  const trackNav = useNavTracker();

  // A hash link only scrolls by itself on a full page load. On a client-side
  // navigation the browser does nothing, and scrolling to top on every route
  // change then actively fights it — so `/#features` landed nowhere, and did
  // nothing at all when the visitor was already on the landing page.
  React.useEffect(() => {
    const { hash } = location;

    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }

    const id = decodeURIComponent(hash.slice(1));
    const smooth = !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    let attempts = 0;
    let frame = 0;

    // The target may still be behind a lazy route chunk, so retry for a beat
    // rather than giving up on the first miss.
    const scrollToTarget = () => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
        return;
      }
      attempts += 1;
      if (attempts < 30) frame = requestAnimationFrame(scrollToTarget);
    };

    frame = requestAnimationFrame(scrollToTarget);
    return () => cancelAnimationFrame(frame);
  }, [location]);

  // Marketing surfaces are dark-only — force `dark` regardless of the app theme.
  return (
    <div className="dark marketing-theme font-geist min-h-screen bg-background text-foreground selection:bg-lime selection:text-lime-foreground">
      {/* Navbar */}
      {/* Keyboard users should not have to tab the whole navigation on every
          page. Visible only once focused. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-md focus:bg-lime focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-lime-foreground"
      >
        Skip to content
      </a>
      <header className="hairline-b fixed top-0 left-0 right-0 z-50 bg-background/75 backdrop-blur-xl">
        <nav aria-label="Main">
        <PublicContainer>
          <div className="flex h-16 items-center justify-between">
            {/* Logo + nav */}
            <div className="flex items-center gap-8">
              <Logo placement="header_nav" />
              <div className="hidden lg:flex items-center gap-6">
                {NAV_LINKS.map((item) => (
                  <PublicNavLink
                    key={item.label}
                    item={item}
                    placement="header_nav"
                    className="rounded-sm text-[13px] text-muted-foreground transition-colors hover:text-foreground active:text-lime"
                  />
                ))}
              </div>
            </div>

            {/* Right cluster */}
            <div className="hidden lg:flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger className="mono inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12px] text-muted-foreground transition-colors hover:bg-surface-2 hover:text-foreground focus:outline-none">
                  <Globe className="h-3.5 w-3.5" /> {language.toUpperCase()}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="dark marketing-theme min-w-[10rem] border-hairline bg-surface-2 text-foreground">
                  {LANGUAGES.map((l) => (
                    <DropdownMenuItem
                      key={l.code}
                      onClick={() => setLanguage(l.code)}
                      className="flex items-center justify-between gap-3 text-[13px] focus:bg-surface-3"
                    >
                      <span className="flex items-center gap-2">
                        <span className="mono text-[11px] text-muted-foreground">{l.code.toUpperCase()}</span>
                        {l.label}
                      </span>
                      {language === l.code && <Check className="h-3.5 w-3.5 text-lime" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {user ? (
                <Link
                  to="/dashboard"
                  className="inline-flex items-center gap-2 rounded-md bg-lime px-4 py-2 text-[13px] font-medium text-lime-foreground transition-all hover:brightness-105 active:scale-[0.98]"
                  onClick={() => trackNav({ to: '/dashboard', placement: 'header_nav' })}
                >
                  Dashboard <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="px-3 py-1.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => trackCta({ action: 'login', placement: 'header_nav', priority: 'secondary' })}
                  >
                    Log in
                  </Link>
                  <Link
                    to={WAITLIST_MODE ? '/waitlist' : '/signup'}
                    className="inline-flex items-center gap-2 rounded-md bg-lime px-4 py-2 text-[13px] font-medium text-lime-foreground transition-all hover:brightness-105 active:scale-[0.98]"
                    onClick={() => trackCta({ action: WAITLIST_MODE ? 'waitlist' : 'signup', placement: 'header_nav', priority: 'primary' })}
                  >
                    {WAITLIST_MODE ? 'Join waitlist' : 'Start free'} <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-controls="public-mobile-menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </PublicContainer>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div id="public-mobile-menu" className="lg:hidden hairline-t bg-background px-6 py-6">
            <div className="flex flex-col gap-5">
              {NAV_LINKS.map((item) => (
                <PublicNavLink
                  key={item.label}
                  item={item}
                  placement="mobile_nav"
                  className="rounded-sm text-base font-medium text-foreground transition-colors hover:text-lime active:text-lime"
                  onClick={() => setIsMobileMenuOpen(false)}
                />
              ))}

              <div className="flex items-center gap-3 py-1">
                <span className="mono inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
                  <Globe className="h-3.5 w-3.5" /> Language
                </span>
                <div className="flex items-center gap-1.5">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { setLanguage(l.code); }}
                      className={cn(
                        'mono rounded-md px-2 py-1 text-[12px] transition-colors',
                        language === l.code
                          ? 'bg-lime text-lime-foreground'
                          : 'text-muted-foreground hover:bg-surface-2 hover:text-foreground',
                      )}
                    >
                      {l.code.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              <div className="hairline-t pt-4 flex flex-col gap-3">
                {user ? (
                  <Link
                    to="/dashboard"
                    className="w-full text-center rounded-md bg-lime px-5 py-2.5 text-sm font-medium text-lime-foreground"
                    onClick={() => {
                      trackNav({ to: '/dashboard', placement: 'mobile_nav' });
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="w-full text-center rounded-md border border-hairline px-5 py-2.5 text-sm font-medium text-foreground"
                      onClick={() => {
                        trackCta({ action: 'login', placement: 'mobile_nav', priority: 'secondary' });
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      Log in
                    </Link>
                    <Link
                      to={WAITLIST_MODE ? '/waitlist' : '/signup'}
                      className="w-full text-center rounded-md bg-lime px-5 py-2.5 text-sm font-medium text-lime-foreground"
                      onClick={() => {
                        trackCta({ action: WAITLIST_MODE ? 'waitlist' : 'signup', placement: 'mobile_nav', priority: 'primary' });
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      {WAITLIST_MODE ? 'Join waitlist' : 'Start free'}
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        </nav>
      </header>

      {/* Page content. Focusable so the skip link moves focus, not just scroll. */}
      <main id="main-content" tabIndex={-1} className="pt-16 focus:outline-none">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="hairline-t bg-surface-1 mt-12 md:mt-24">
        <PublicContainer className="py-10 md:py-16">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-4">
            <div className="sm:col-span-2 md:col-span-1">
              <Logo placement="footer" />
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed max-w-xs">
                Automated technical debt detection and remediation for modern engineering teams.
              </p>
            </div>

            <div>
              <h3 className="mono text-[11px] uppercase tracking-wider text-muted-foreground mb-4">Product</h3>
              <ul className="space-y-3">
                <li><FooterLink to="/#features">Product</FooterLink></li>
                {!WAITLIST_MODE && <li><FooterLink to="/pricing">Pricing</FooterLink></li>}
                {!WAITLIST_MODE && (
                  <li>
                    <FooterLink to="/docs" cta={{ action: 'product_docs', priority: 'secondary' }}>
                      Documentation
                    </FooterLink>
                  </li>
                )}
                <li>
                  <FooterLink to={CLI_DOCS_URL} external cta={{ action: 'cli_docs', priority: 'secondary' }}>
                    CLI
                  </FooterLink>
                </li>
                {!WAITLIST_MODE && <li><FooterLink to="/technical-debt-scanner">Technical debt scanning</FooterLink></li>}
                <li><FooterLink to="/sample-report">Sample report</FooterLink></li>
                {!WAITLIST_MODE && <li><FooterLink to="/ci-cd-quality-gates">CI/CD quality gates</FooterLink></li>}
                {!WAITLIST_MODE && <li><FooterLink to="/roadmap">Roadmap</FooterLink></li>}
                {!WAITLIST_MODE && <li><FooterLink to="/blog">Blog</FooterLink></li>}
                {!WAITLIST_MODE && <li><FooterLink to="/changelog">Changelog</FooterLink></li>}
              </ul>
            </div>

            <div>
              <h3 className="mono text-[11px] uppercase tracking-wider text-muted-foreground mb-4">Company</h3>
              <ul className="space-y-3">
                <li>
                  <FooterLink to="/login" cta={{ action: 'login', priority: 'secondary' }}>
                    Log in
                  </FooterLink>
                </li>
                <li>
                  <FooterLink
                    to={WAITLIST_MODE ? '/waitlist' : '/signup'}
                    cta={{ action: WAITLIST_MODE ? 'waitlist' : 'signup', priority: 'primary' }}
                  >
                    {WAITLIST_MODE ? 'Join waitlist' : 'Sign Up'}
                  </FooterLink>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="mono text-[11px] uppercase tracking-wider text-muted-foreground mb-4">Legal</h3>
              <ul className="space-y-3">
                <li><FooterLink to="/legal/privacy">Privacy Policy</FooterLink></li>
                <li><FooterLink to="/legal/terms">Terms of Service</FooterLink></li>
                <li><FooterLink to="/legal/refund">Refund Policy</FooterLink></li>
              </ul>
            </div>
          </div>

          <div className="hairline-t mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="mono text-[11px] text-muted-foreground">
              © {new Date().getFullYear()} DebtDrone. All rights reserved.
            </p>
            <p className="mono text-[11px] text-muted-foreground">
              Built for engineers who care about code quality.
            </p>
          </div>
        </PublicContainer>
      </footer>
    </div>
  );
};

export default PublicLayout;
